import { useMemo } from 'react'
import type { TabOverrides } from '@/core/components/modal/ModalHeader'
import {
  ShellFormularBox,
  type ShellFormularApi,
  type ShellFormularDaten,
  type ShellFormularTexte,
} from '@/core/components/form/ShellFormularBox'
import type { Shell, ShellFeld } from '@/core/templates/types'
import {
  BOGEN_FEHLT,
  SprachniveauAbweichungHinweis,
  SprachniveauMarker,
  useSprachniveauStand,
} from '../sprachniveau/sprachniveau'
import {
  getFoerderplan,
  type Foerderplan as FoerderplanStand,
  fetchFoerderplanVorschau,
  saveFoerderplan,
  speichereFoerderplanEntwurf,
  foerderplanProbleme,
} from '../../api/foerderplan'
import { useUnterschriftOeffnen } from '../unterschrift/use-unterschrift-oeffnen'
import { UnterschriftBox, unterschreibenText } from '../unterschrift/UnterschriftBox'
import { useUnterschriften } from '../unterschrift/use-unterschriften'
import { KompetenzbilanzierungSperre, useKompetenzbilanzierungGespeichert } from '../kompetenzfeststellung/Kompetenzbilanzierung'

const SPRACHSTAND = 'sprachstand'

const istSprachstand = (feld: ShellFeld | null): boolean => feld?.id === SPRACHSTAND

const SPRACHSTAND_FROZEN: ReadonlySet<string> = new Set([SPRACHSTAND])

function sprachstandNurLesen(schale: Shell): Shell {
  return {
    ...schale,
    abschnitte: schale.abschnitte.map((abschnitt) =>
      abschnitt.felder
        ? {
            ...abschnitt,
            felder: abschnitt.felder.map((feld) => (istSprachstand(feld) ? { ...feld, nur_lesen: true } : feld)),
          }
        : abschnitt,
    ),
  }
}

function hinweiseWieEntschieden(hinweise: FoerderplanStand['hinweise']): ShellFormularDaten['hinweise'] {
  return hinweise[SPRACHSTAND] === undefined ? hinweise : { ...hinweise, [SPRACHSTAND]: BOGEN_FEHLT }
}

const API: ShellFormularApi = {
  laden: async (coachingId) => {
    const stand = await getFoerderplan(coachingId)
    return {
      ...stand,
      schale: sprachstandNurLesen(stand.schale as Shell),
      hinweise: hinweiseWieEntschieden(stand.hinweise),
      anmerkungen: {
        [SPRACHSTAND]: <SprachniveauAbweichungHinweis dokument="foerderplan" abweichung={stand.sprachniveau_abweichung} />,
      },
    }
  },
  vorschau: (coachingId, werte) => fetchFoerderplanVorschau(coachingId, werte),
  speichern: (coachingId, werte) => saveFoerderplan(coachingId, werte),
  entwurfSpeichern: (coachingId, werte) => speichereFoerderplanEntwurf(coachingId, werte),
  probleme: (error) => foerderplanProbleme(error),
}

const TEXTE: ShellFormularTexte = {
  name: "Förderplan",
  gespeichert: "Förderplan gespeichert",
  ladenFehlgeschlagen: "Der Förderplan konnte nicht geladen werden.",
  speichernFehlgeschlagen: "Der Förderplan konnte nicht gespeichert werden.",
}

export function Foerderplan({ coachingId, tabs }: { coachingId: string | null; tabs?: TabOverrides }) {
  const stand = useUnterschriften(coachingId, 'foerderplan', "Förderplan")
  const spalte = useSprachniveauStand(coachingId, typeof stand === 'object' && stand !== null ? stand.dokumentId : null)
  const kompetenzbilanzierung = useKompetenzbilanzierungGespeichert(coachingId)
  if (stand === null) {
    if (kompetenzbilanzierung !== true) return <KompetenzbilanzierungSperre gespeichert={kompetenzbilanzierung} tabs={tabs} />
    return <FoerderplanFormular coachingId={coachingId} tabs={tabs} />
  }
  return (
    <UnterschriftBox
      dokument="foerderplan"
      coachingId={coachingId}
      tabs={tabs}
      stand={stand}
      marker={
        <>
          <SprachniveauAbweichungHinweis dokument="foerderplan" abweichung={spalte?.sprachniveau_abweichung} />
          <SprachniveauMarker dokument="foerderplan" stand={spalte?.sprachniveau?.foerderplan} />
        </>
      }
    />
  )
}

function FoerderplanFormular({ coachingId, tabs }: { coachingId: string | null; tabs?: TabOverrides }) {
  const oeffnen = useUnterschriftOeffnen('foerderplan')
  const api = useMemo<ShellFormularApi>(() => ({ ...API, nachSpeichern: (id) => oeffnen(id) }), [oeffnen])
  return (
    <ShellFormularBox
      coachingId={coachingId}
      api={api}
      texte={TEXTE}
      tabs={tabs}
      ueberschrift
      speichernText={unterschreibenText('foerderplan')}
      frozenFields={SPRACHSTAND_FROZEN}
    />
  )
}
